import {
    Box,
    Calendar,
    IconButton,
    QuickFilterButton,
    Search,
    TableSidePanel,
    TabMenu,
    TabMenuItem,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

import style from "./side-panel.scss";
import { SidePanelDailyQuota } from "./SidePanelDailyQuota";
import { SidePanelInfo } from "./SidePanelInfo";
import { SidePanelLogs } from "./SidePanelLogs";
import SidePanelVersionHistory, { RestoreFunction } from "./SidePanelVersionHistory";
import { CalendarIcon } from "components/icons/CalendarIcon";
import Exit from "components/icons/Exit";
import NewSearchIcon from "components/icons/NewSearchIcon";
import TriggerIcon from "components/icons/TriggerIcon";
import BmpQuickFilterButton from "components/table/QuickFilterButton";
import BmpTooltip from "components/tooltip/Tooltip";
import { AUTH_WORKFLOW_SECURED } from "domain/authority";
import { FilterState, LaunchType, ScheduleStatus, WorkflowEntityType, WorkflowTableData } from "domain/workflows";
import { TemplateTableData } from "domain/workflowTemplates";
import { featureFlagService, FLAG_UICOMPS_WORKFLOWS_TABLE } from "services/feature/FeatureFlagService";
import { isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import {
    getWorkflowAutoRefreshState,
    setWorkflowAutoRefreshState,
} from "services/workflows/workflowAutoRefreshRepository";
import { Profile, State, WorkflowEditorV3SupportedEntry } from "services/workflows/WorkflowService";
import { resolveColor } from "utils/commonFunctions";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

enum TabType {
    LOG = "LOG",
    TEST = "TEST",
    INFO = "INFO",
    DAILY_QUOTA = "DAILY_QUOTA",
    VERSION_HISTORY = "VERSION_HISTORY",
}

interface SidePanelProps {
    isSidePanelOpen: boolean;
    onClose: () => void;
    selectedRow: WorkflowTableData | TemplateTableData | undefined;
    setFilterState?: React.Dispatch<React.SetStateAction<FilterState>>;
    testId: string;
    type: WorkflowEntityType;
    isSubWorkflow?: boolean;
    onRestoreVersion?: RestoreFunction;
    onOpenWithV3?: (workflowUuid: string, versionId: string) => void;
    workflowEditorV3Supported?: WorkflowEditorV3SupportedEntry[];
    onRefreshTable?: () => void;
    onCloseSidePanel?: () => void;
}

export function SidePanel(props: SidePanelProps): JSX.Element | null {
    if (!props.isSidePanelOpen || !props.selectedRow) {
        return null;
    }
    const { t } = useTranslation();

    // TODO BCC-8002: Remove after Workflows table rewrite is complete.
    const isBUICWorkflowsTable =
        featureFlagService.isFeatureEnabled(FLAG_UICOMPS_WORKFLOWS_TABLE) && "createdDate" in props.selectedRow;

    const filters = [State.ALL, State.FAILED, State.SUCCEEDED];
    const theme = useTheme();
    const [activeButton, setActiveButton] = React.useState<State>(State.ALL);
    const [showSearch, setShowSearch] = React.useState(false);
    const [showDateFilter, setShowDateFilter] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState<TabType>(TabType.LOG);

    const handleFilterClick = (filter: State) => {
        setActiveButton(filter);
    };

    const [filterState, setFilterState] = React.useState<FilterState>({
        from: "",
        to: "",
    });

    const [autoRefresh, setAutoRefreshState] = React.useState(() =>
        getWorkflowAutoRefreshState(props.selectedRow?.uuid || "")
    );

    React.useEffect(() => {
        if (props.selectedRow?.uuid) {
            const storedState = getWorkflowAutoRefreshState(props.selectedRow.uuid);
            setAutoRefreshState(storedState);
        }
    }, [props.selectedRow?.uuid]);

    const setAutoRefresh = (enabled: boolean) => {
        if (props.selectedRow?.uuid) {
            setWorkflowAutoRefreshState(props.selectedRow.uuid, enabled);
            setAutoRefreshState(enabled);
        }
    };
    const [refreshCooldown, setRefreshCooldown] = React.useState(false);

    React.useEffect(() => {
        const isTemplateOrSubWorkflow =
            props.type === WorkflowEntityType.TEMPLATE || props.type === WorkflowEntityType.SUB_WORKFLOW;
        const isBmpWorkflow = props.selectedRow?.profile.toLowerCase() === Profile.BMP;

        let defaultTab = TabType.INFO;

        if (props.type === WorkflowEntityType.WORKFLOW && isBmpWorkflow) {
            defaultTab = TabType.LOG;
        } else if (isTemplateOrSubWorkflow && isBmpWorkflow) {
            defaultTab = TabType.TEST;
        }

        setActiveTab(defaultTab);
        setActiveButton(State.ALL);
        setShowSearch(false);
        setShowDateFilter(false);
        setFilterState({ from: "", to: "" });
    }, [props.selectedRow?.uuid, props.type]);

    const filterTestIds = new Map<State, string>([
        [State.ALL, testIds.workArea.workflows.sidePanel.tabs.log.quickFilter.all],
        [State.FAILED, testIds.workArea.workflows.sidePanel.tabs.log.quickFilter.failed],
        [State.SUCCEEDED, testIds.workArea.workflows.sidePanel.tabs.log.quickFilter.succeeded],
    ]);

    const filterTranslationKeys = new Map<State, string>([
        [State.ALL, "WorkflowsTable.sidePanel.all"],
        [State.SUCCEEDED, "WorkflowsTable.sidePanel.success"],
        [State.FAILED, "WorkflowsTable.sidePanel.failure"],
    ]);

    const panelClassNames = classNames(style.sidePanel, {
        [style.open]: props.isSidePanelOpen,
    });

    const isSubWorkflow = props.type === WorkflowEntityType.SUB_WORKFLOW;
    const isBmpWorkflow = props.selectedRow?.profile.toLowerCase() === Profile.BMP;

    const showTestTab =
        isBmpWorkflow &&
        (!props.selectedRow.secured ||
            (isCurrentUserInternal() && userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SECURED])));

    const showLogTab = props.type === WorkflowEntityType.WORKFLOW && isBmpWorkflow;

    const showVersionHistoryTab =
        props.type === WorkflowEntityType.WORKFLOW || props.type === WorkflowEntityType.TEMPLATE || isSubWorkflow;

    const visibleTabs = [
        ...(showLogTab ? [TabType.LOG] : []),
        ...(showTestTab ? [TabType.TEST] : []),
        ...(isBmpWorkflow ? [TabType.DAILY_QUOTA] : []),
        TabType.INFO,
        ...(showVersionHistoryTab ? [TabType.VERSION_HISTORY] : []),
    ];

    const shouldShowTabs = visibleTabs.length > 1;

    const connectedWorkflows = props.selectedRow!.connectedWorkflows;

    const isLogOrTestTabActive = () => activeTab === TabType.LOG || activeTab === TabType.TEST;
    const isTestTabActive = () => activeTab === TabType.TEST;

    const getTabTestIds = () => {
        const isTemplate = props.type === WorkflowEntityType.TEMPLATE || props.type === WorkflowEntityType.SUB_WORKFLOW;

        if (isTemplate) {
            return {
                info: testIds.workArea.workflowTemplates.sidePanel.tabs.info.itself,
                test: testIds.workArea.workflowTemplates.sidePanel.tabs.test.itself,
            };
        } else {
            return {
                info: testIds.workArea.workflows.sidePanel.tabs.info.itself,
                test: testIds.workArea.workflows.sidePanel.tabs.test.itself,
            };
        }
    };

    const tabTestIds = getTabTestIds();

    // TODO BCC-8002: Remove this if clause after Workflows table rewrite is complete
    if (!isBUICWorkflowsTable) {
        return (
            <div className={panelClassNames} data-testid={props.testId}>
                {isBmpWorkflow && (
                    <div className={style.triggerContainer}>
                        <div className={style.trigger}>
                            <div className={style.tiggerIcon}>
                                {" "}
                                <TriggerIcon color={theme.defaultStarColor} />
                            </div>
                            <p>
                                {t("WorkflowsTable.sidePanel.launchType.triggerLabel")}
                                {(() => {
                                    const isTemplate =
                                        props.type === WorkflowEntityType.TEMPLATE ||
                                        props.type === WorkflowEntityType.SUB_WORKFLOW;
                                    const hasActiveSchedule = isTemplate
                                        ? (props.selectedRow as TemplateTableData).schedule !== undefined
                                        : (props.selectedRow as WorkflowTableData).schedule === ScheduleStatus.ACTIVE;

                                    if (props.selectedRow!.launchType === LaunchType.MANUAL) {
                                        return hasActiveSchedule
                                            ? t("WorkflowsTable.sidePanel.launchType.manuallyAndScheduled")
                                            : t("WorkflowsTable.sidePanel.launchType.manually");
                                    } else {
                                        const workflowRow = props.selectedRow as WorkflowTableData;
                                        switch (workflowRow.launchSource) {
                                            case "ALL":
                                                return t("WorkflowsTable.sidePanel.launchType.reportUpload");
                                            case "API":
                                                return t("WorkflowsTable.sidePanel.launchType.automaticReportUpload");
                                            case "UI":
                                                return t("WorkflowsTable.sidePanel.launchType.manualReportUpload");
                                            default:
                                                return workflowRow.launchSource;
                                        }
                                    }
                                })()}
                            </p>
                        </div>
                    </div>
                )}

                <h2 className={style.headerName}>
                    {props.selectedRow.name ?? t("WorkflowsTable.sidePanel.unknownWorkflow")}
                </h2>
                {shouldShowTabs ? (
                    <>
                        <Tabs
                            selectedIndex={visibleTabs.indexOf(activeTab)}
                            onSelect={(index) => setActiveTab(visibleTabs[index])}
                        >
                            <TabList>
                                {showLogTab && (
                                    <Tab data-testid={testIds.workArea.workflows.sidePanel.tabs.log.itself}>
                                        {t("WorkflowsTable.sidePanel.tabs.log.logButton")}
                                    </Tab>
                                )}
                                {showTestTab && (
                                    <Tab data-testid={tabTestIds.test}>
                                        {t("WorkflowsTable.sidePanel.tabs.test.testButton")}
                                    </Tab>
                                )}
                                {isBmpWorkflow && (
                                    <Tab data-testid={testIds.workArea.workflows.sidePanel.tabs.dailyQuota.itself}>
                                        {t("WorkflowsTable.sidePanel.tabs.dailyQuota.dailyQuotaButton")}
                                    </Tab>
                                )}
                                {<Tab data-testid={tabTestIds.info}>{t("Common.info")}</Tab>}
                                {showVersionHistoryTab && (
                                    <Tab data-testid={testIds.workArea.workflows.sidePanel.tabs.versionHistory.itself}>
                                        {t("ManageWorkflowDialog.versionList.title")}
                                    </Tab>
                                )}
                            </TabList>
                            {/*
                                These empty TabPanels are intentional and necessary for proper tab indexing.
                                The Live runs, Test, and Version history tab content is rendered outside the Tabs component due to CSS inheritance issues
                                as the TabPanel causes the parent-child css inheritance to break between the SidePanel and its child components
                                and further causing the content to overflow out of the SidePanel, but the react tabs still requires TabPanel
                                elements to maintain correct tab to content mapping.
                            */}
                            {showLogTab && <TabPanel></TabPanel>}
                            {showTestTab && <TabPanel></TabPanel>}
                            {isBmpWorkflow && (
                                <TabPanel>
                                    <SidePanelDailyQuota />
                                </TabPanel>
                            )}
                            {
                                <TabPanel>
                                    <SidePanelInfo
                                        connectedWorkflows={connectedWorkflows}
                                        product={
                                            props.type === WorkflowEntityType.WORKFLOW
                                                ? (props.selectedRow as WorkflowTableData).product
                                                : undefined
                                        }
                                        description={props.selectedRow.description}
                                        version={props.selectedRow?.editorVersion || ""}
                                        type={props.type}
                                        selectedRow={props.selectedRow}
                                        isSubWorkflow={props.isSubWorkflow}
                                    />
                                </TabPanel>
                            }
                            {showVersionHistoryTab && <TabPanel></TabPanel>}
                        </Tabs>

                        {isLogOrTestTabActive() && (
                            <>
                                <div className={style.filterContainer}>
                                    <div
                                        className={style.quickFilterButtonsContainer}
                                        data-testid={testIds.workArea.workflows.sidePanel.tabs.log.quickFilter.itself}
                                    >
                                        {filters.map((filter) => (
                                            <BmpQuickFilterButton
                                                onClick={() => handleFilterClick(filter)}
                                                key={filter}
                                                filter={t(
                                                    filterTranslationKeys.get(filter) || "WorkflowsTable.sidePanel.all"
                                                )}
                                                isActive={activeButton === filter}
                                                testId={
                                                    filterTestIds.get(filter) ||
                                                    testIds.workArea.workflows.sidePanel.tabs.log.quickFilter.all
                                                }
                                            />
                                        ))}
                                    </div>
                                    <div
                                        className={classNames(style.rightFilter, {
                                            [style.disabled]: showSearch || showDateFilter,
                                        })}
                                    >
                                        <BmpTooltip content={t("WorkflowsTable.sidePanel.searchByDate")}>
                                            <div
                                                className={style.calendarContainer}
                                                onClick={() => setShowDateFilter(true)}
                                            >
                                                <CalendarIcon color={resolveColor("--theme-icon-fill-default")} />
                                            </div>
                                        </BmpTooltip>
                                        <BmpTooltip content={t("WorkflowsTable.sidePanel.searchByReportUuid")}>
                                            <div className={style.icon} onClick={() => setShowSearch(true)}>
                                                <NewSearchIcon color={theme.iconFillColor} />
                                            </div>
                                        </BmpTooltip>
                                    </div>
                                </div>
                                <SidePanelLogs
                                    key={activeTab}
                                    filterState={filterState}
                                    setFilterState={setFilterState}
                                    showDateFilter={showDateFilter}
                                    showSearch={showSearch}
                                    filter={activeButton}
                                    testMode={isTestTabActive()}
                                    setShowSearch={() => setShowSearch(false)}
                                    setShowDateFilter={() => setShowDateFilter(false)}
                                    workflowUuid={props.selectedRow?.uuid || ""}
                                    autoRefresh={autoRefresh}
                                    setAutoRefresh={setAutoRefresh}
                                    refreshCooldown={refreshCooldown}
                                    setRefreshCooldown={setRefreshCooldown}
                                    enabled={
                                        props.type === WorkflowEntityType.WORKFLOW
                                            ? (props.selectedRow as WorkflowTableData).enabled
                                            : undefined
                                    }
                                    isTemplate={props.type === WorkflowEntityType.TEMPLATE || isSubWorkflow}
                                    launchType={(props.selectedRow as WorkflowTableData).launchType}
                                    secured={props.selectedRow.secured}
                                />
                            </>
                        )}
                        {activeTab === TabType.VERSION_HISTORY && (
                            <SidePanelVersionHistory
                                currentVersion={{
                                    version: props.selectedRow.editorVersion || "",
                                    modifiedDate: (props.selectedRow as TemplateTableData).modified || "",
                                }}
                                workflowUuid={props.selectedRow.uuid}
                                entityType={props.type}
                                selectedRow={props.selectedRow}
                                onRestoreVersion={props.onRestoreVersion}
                                onOpenWithV3={props.onOpenWithV3}
                                workflowEditorV3Supported={props.workflowEditorV3Supported}
                                onRefreshTable={props.onRefreshTable}
                                onCloseSidePanel={props.onCloseSidePanel}
                            />
                        )}
                    </>
                ) : (
                    <SidePanelInfo
                        connectedWorkflows={connectedWorkflows}
                        product={
                            props.type === WorkflowEntityType.WORKFLOW
                                ? (props.selectedRow as WorkflowTableData).product
                                : undefined
                        }
                        description={props.selectedRow?.description || ""}
                        version={props.selectedRow?.editorVersion || ""}
                        type={props.type}
                        selectedRow={props.selectedRow}
                        isSubWorkflow={props.isSubWorkflow}
                    />
                )}

                {!isBUICWorkflowsTable && (
                    <button role="close" onClick={props.onClose} data-testid={testIds.common.dialog.xButton}>
                        <Exit color={theme.iconFillColor} />
                    </button>
                )}
            </div>
        );
    }

    return (
        // TODO BCC-8002: Rewrite side panel contents with BUIC components
        <TableSidePanel
            onClose={props.onClose}
            heading={props.selectedRow.name ?? t("WorkflowsTable.sidePanel.unknownWorkflow")}
            statusBadge={
                isBmpWorkflow && (
                    <div className={style.trigger}>
                        <div className={style.tiggerIcon}>
                            <TriggerIcon color={theme.defaultStarColor} />
                        </div>
                        <p>
                            {t("WorkflowsTable.sidePanel.launchType.triggerLabel")}
                            {(() => {
                                const isTemplate =
                                    props.type === WorkflowEntityType.TEMPLATE ||
                                    props.type === WorkflowEntityType.SUB_WORKFLOW;
                                const hasActiveSchedule = isTemplate
                                    ? (props.selectedRow as TemplateTableData).schedule !== undefined
                                    : (props.selectedRow as WorkflowTableData).schedule === ScheduleStatus.ACTIVE;

                                if (props.selectedRow!.launchType === LaunchType.MANUAL) {
                                    return hasActiveSchedule
                                        ? t("WorkflowsTable.sidePanel.launchType.manuallyAndScheduled")
                                        : t("WorkflowsTable.sidePanel.launchType.manually");
                                } else {
                                    const workflowRow = props.selectedRow as WorkflowTableData;
                                    switch (workflowRow.launchSource) {
                                        case "ALL":
                                            return t("WorkflowsTable.sidePanel.launchType.reportUpload");
                                        case "API":
                                            return t("WorkflowsTable.sidePanel.launchType.automaticReportUpload");
                                        case "UI":
                                            return t("WorkflowsTable.sidePanel.launchType.manualReportUpload");
                                        default:
                                            return workflowRow.launchSource;
                                    }
                                }
                            })()}
                        </p>
                    </div>
                )
            }
        >
            {shouldShowTabs ? (
                <>
                    <TabMenu>
                        {showLogTab && (
                            <TabMenuItem
                                key={visibleTabs.indexOf(TabType.LOG)}
                                isActive={activeTab === TabType.LOG}
                                onClick={() => setActiveTab(TabType.LOG)}
                                testId={testIds.workArea.workflows.sidePanel.tabs.log.itself}
                            >
                                {t("WorkflowsTable.sidePanel.tabs.log.logButton")}
                            </TabMenuItem>
                        )}
                        {showTestTab && (
                            <TabMenuItem
                                key={visibleTabs.indexOf(TabType.TEST)}
                                isActive={activeTab === TabType.TEST}
                                onClick={() => setActiveTab(TabType.TEST)}
                                testId={tabTestIds.test}
                            >
                                {t("WorkflowsTable.sidePanel.tabs.test.testButton")}
                            </TabMenuItem>
                        )}
                        {isBmpWorkflow && (
                            <TabMenuItem
                                key={visibleTabs.indexOf(TabType.DAILY_QUOTA)}
                                isActive={activeTab === TabType.DAILY_QUOTA}
                                onClick={() => setActiveTab(TabType.DAILY_QUOTA)}
                                testId={testIds.workArea.workflows.sidePanel.tabs.dailyQuota.itself}
                            >
                                {t("WorkflowsTable.sidePanel.tabs.dailyQuota.dailyQuotaButton")}
                            </TabMenuItem>
                        )}
                        <TabMenuItem
                            key={visibleTabs.indexOf(TabType.INFO)}
                            isActive={activeTab === TabType.INFO}
                            onClick={() => setActiveTab(TabType.INFO)}
                            testId={tabTestIds.info}
                        >
                            {t("Common.info")}
                        </TabMenuItem>
                        {showVersionHistoryTab && (
                            <TabMenuItem
                                key={visibleTabs.indexOf(TabType.VERSION_HISTORY)}
                                isActive={activeTab === TabType.VERSION_HISTORY}
                                onClick={() => setActiveTab(TabType.VERSION_HISTORY)}
                                testId={testIds.workArea.workflows.sidePanel.tabs.versionHistory.itself}
                            >
                                {t("ManageWorkflowDialog.versionList.title")}
                            </TabMenuItem>
                        )}
                    </TabMenu>

                    {activeTab === TabType.DAILY_QUOTA && <SidePanelDailyQuota />}

                    {activeTab === TabType.INFO && (
                        <SidePanelInfo
                            connectedWorkflows={connectedWorkflows}
                            product={
                                props.type === WorkflowEntityType.WORKFLOW
                                    ? (props.selectedRow as WorkflowTableData).product
                                    : undefined
                            }
                            description={props.selectedRow.description}
                            version={props.selectedRow?.editorVersion || ""}
                            type={props.type}
                            selectedRow={props.selectedRow}
                            isSubWorkflow={props.isSubWorkflow}
                        />
                    )}

                    {isLogOrTestTabActive() && (
                        <>
                            <Box justifyContent="SpaceBetween">
                                <Box
                                    gap="Xxs"
                                    testId={testIds.workArea.workflows.sidePanel.tabs.log.quickFilter.itself}
                                >
                                    {filters.map((filter) => (
                                        <QuickFilterButton
                                            key={filter}
                                            isActive={activeButton === filter}
                                            onClick={() => handleFilterClick(filter)}
                                            testId={
                                                filterTestIds.get(filter) ||
                                                testIds.workArea.workflows.sidePanel.tabs.log.quickFilter.all
                                            }
                                        >
                                            {t(filterTranslationKeys.get(filter) || "WorkflowsTable.sidePanel.all")}
                                        </QuickFilterButton>
                                    ))}
                                </Box>

                                <Box gap="Xxs">
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <IconButton
                                                onClick={() => {
                                                    setShowSearch(false);
                                                    setShowDateFilter(true);
                                                }}
                                                disabled={showDateFilter}
                                            >
                                                <Calendar />
                                            </IconButton>
                                        </TooltipTrigger>
                                        <TooltipContent>{t("WorkflowsTable.sidePanel.searchByDate")}</TooltipContent>
                                    </Tooltip>

                                    <Tooltip>
                                        <TooltipTrigger>
                                            <IconButton
                                                onClick={() => {
                                                    setShowDateFilter(false);
                                                    setShowSearch(true);
                                                }}
                                                disabled={showSearch}
                                            >
                                                <Search />
                                            </IconButton>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {t("WorkflowsTable.sidePanel.searchByReportUuid")}
                                        </TooltipContent>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <SidePanelLogs
                                key={activeTab}
                                filterState={filterState}
                                setFilterState={setFilterState}
                                showDateFilter={showDateFilter}
                                showSearch={showSearch}
                                filter={activeButton}
                                testMode={isTestTabActive()}
                                setShowSearch={() => setShowSearch(false)}
                                setShowDateFilter={() => setShowDateFilter(false)}
                                workflowUuid={props.selectedRow?.uuid || ""}
                                autoRefresh={autoRefresh}
                                setAutoRefresh={setAutoRefresh}
                                refreshCooldown={refreshCooldown}
                                setRefreshCooldown={setRefreshCooldown}
                                enabled={
                                    props.type === WorkflowEntityType.WORKFLOW
                                        ? (props.selectedRow as WorkflowTableData).enabled
                                        : undefined
                                }
                                isTemplate={props.type === WorkflowEntityType.TEMPLATE || isSubWorkflow}
                                launchType={(props.selectedRow as WorkflowTableData).launchType}
                                secured={props.selectedRow.secured}
                            />
                        </>
                    )}
                    {activeTab === TabType.VERSION_HISTORY && (
                        <SidePanelVersionHistory
                            currentVersion={{
                                version: props.selectedRow.editorVersion || "",
                                modifiedDate: (props.selectedRow as TemplateTableData).modified || "",
                            }}
                            workflowUuid={props.selectedRow.uuid}
                            entityType={props.type}
                            selectedRow={props.selectedRow}
                            onRestoreVersion={props.onRestoreVersion}
                            onOpenWithV3={props.onOpenWithV3}
                            workflowEditorV3Supported={props.workflowEditorV3Supported}
                            onRefreshTable={props.onRefreshTable}
                            onCloseSidePanel={props.onCloseSidePanel}
                        />
                    )}
                </>
            ) : (
                <SidePanelInfo
                    connectedWorkflows={connectedWorkflows}
                    product={
                        props.type === WorkflowEntityType.WORKFLOW
                            ? (props.selectedRow as WorkflowTableData).product
                            : undefined
                    }
                    description={props.selectedRow?.description || ""}
                    version={props.selectedRow?.editorVersion || ""}
                    type={props.type}
                    selectedRow={props.selectedRow}
                    isSubWorkflow={props.isSubWorkflow}
                />
            )}
        </TableSidePanel>
    );
}

export default SidePanel;
